import {
  MDBCard,
  MDBCardBody,
  MDBCol,
  MDBContainer,
  MDBRow,
  MDBSpinner,
  MDBAlert,
  MDBBtn,
  MDBIcon
} from "mdb-react-ui-kit";
import React, { useContext, useState, useCallback, useImperativeHandle, forwardRef } from "react";
import { useTranslation } from 'react-i18next';
import { captureError } from '../../../utils/sentryHelpers';
import { DevContext } from "../../../App.jsx";
import { FormProvider, useFormContext } from "./components/FormProvider.jsx";
import StepRenderer from "./components/StepRenderer.jsx";
import { useValidation, ValidationProvider } from "./components/ValidationProvider.jsx";
import { StepsConfigProvider } from "./contexts/StepsConfigContext.jsx";
import LectureErrorBoundary from "./components/LectureErrorBoundary.jsx";
import { useStepNavigation } from "./hooks/useStepNavigation.js";
import { useLectureAPI } from "./hooks/useLectureAPI.js";
import { useCompanyData } from "./hooks/useCompanyData.js";
import { useIndustry } from "../../../utils/industryContextHook";
import { transformDBToFormFormat, transformFormToDBFormat } from "./utils/lectureDataTransform.js";
import { StepperNavigation } from "./stepper/index.ts";
import { updateStepsArray } from "./utils/stepHelpers.js";
import { ConfigProvider, useConfig } from "./config/ConfigProvider.jsx";
import { getLocalizedText } from "../../../utils/localizedText.js";

// Main component with form context
const LectureRegistrationContent = forwardRef(({ steps, setSteps, companyId, eventId, lectureId, onSaveLecture, onCloseRequest, isModal = false }, ref) => {
  const { t } = useTranslation();
  const { formData, markStepCompleted, markStepUncompleted, loadLectureData, hasUnsavedChanges } = useFormContext();
  const { isFormReadyToSave, getValidationSummary, getStepValidation, isStepComplete } = useValidation();
  const { isDevMode } = useContext(DevContext);
  const { currentIndustry } = useIndustry();
  const { stepsConfig, getEntityLabel } = useConfig();
  
  // Use the lecture API hook
  const { fetchLecture, createLecture, updateLecture, isLoading: isSavingAPI, error: lectureError } = useLectureAPI();
  const { employees } = useCompanyData(companyId);
  
  // State for loading
  const [isLoading, setIsLoading] = React.useState(false);
  const [loadError, setLoadError] = React.useState(null);

  // Store original lecture data for updates (to detect what needs to be deleted)
  const [originalLectureData, setOriginalLectureData] = React.useState(null);

  // Navigation now uses context
  const { currentStep, navigateToStep, canNavigateToStep } = useStepNavigation();
  
  // State for highlighting incomplete steps
  const [highlightIncompleteSteps, setHighlightIncompleteSteps] = React.useState(false);
  
  // Memoized change detection for performance
  const hasChanges = React.useMemo(() => {
    return hasUnsavedChanges?.() || false;
  }, [hasUnsavedChanges, formData]); // Re-calculate when form data changes

  // Check if we're in edit mode
  const isEditMode = lectureId && lectureId !== 'new';

  // Track if lecture data has been loaded
  const [isLectureLoaded, setIsLectureLoaded] = React.useState(false);
  // Track the lecture ID that we've navigated for
  const [navigatedForLectureId, setNavigatedForLectureId] = React.useState(null);
  
  // Reset navigation tracking when lectureId changes
  React.useEffect(() => {
    setNavigatedForLectureId(null);
    setIsLectureLoaded(false);
    setOriginalLectureData(null);
  }, [lectureId]);

  // Load lecture data if in edit mode
  React.useEffect(() => {
    // Prevent duplicate loading
    if (!isEditMode) return;
    
    let isCancelled = false;
    
    const loadExistingLecture = async () => {
      console.log("Loading lecture for editing, ID:", lectureId);
      setIsLoading(true);
      setLoadError(null);
      setIsLectureLoaded(false);
      // Reset navigation tracking when starting a new load
      setNavigatedForLectureId(null);
      
      try {
        // Fetch lecture from API
        const dbLectureData = await fetchLecture(lectureId);

        if (isCancelled) return;

        if (dbLectureData) {
          // Store original data for comparison during updates
          setOriginalLectureData(dbLectureData);

          // Transform DB format to form format
          const formLectureData = transformDBToFormFormat(dbLectureData, employees);
          console.log("Transformed lecture data for form:", formLectureData);

          // Load into form
          loadLectureData(formLectureData);
          // Mark that lecture has been loaded
          setIsLectureLoaded(true);
        } else {
          throw new Error('Lecture not found');
        }
      } catch (error) {
        if (!isCancelled) {
          captureError(error, {
            feature: 'lecture-management',
            operation: 'load-lecture-data',
            consoleMessage: 'Error loading lecture data:',
            context: { lectureId }
          });
          setLoadError(error.message || t('Failed to load lecture data'));
        }
      } finally {
        if (!isCancelled) {
          setIsLoading(false);
        }
      }
    };

    loadExistingLecture();
    
    return () => {
      isCancelled = true;
    };
  }, [lectureId, isEditMode, fetchLecture, loadLectureData, employees, t]);

  // Auto-complete steps based on schema-driven data validation
  React.useEffect(() => {
    // Get all step IDs from the configuration (including substeps)
    let allStepIds = [];

    if (stepsConfig?.length > 0) {
      stepsConfig.forEach(step => {
        // Don't add "details" as it's just a container for substeps
        if (step.id !== 'details') {
          allStepIds.push(step.id);
        }
        // Add substep IDs if they exist
        if (step.substeps) {
          step.substeps.forEach(substep => {
            allStepIds.push(substep.id);
          });
        }
      });
    } else {
      // Fallback list
      allStepIds = [
        'information', 'general_conditions', 'lecture_type', 'conditions',
        'language', 'title', 'subjects', 'description', 'topics',
        'speakers', 'image', 'handout', 'presentation'
      ];
    }

    allStepIds.forEach(stepId => {
      const shouldBeCompleted = isStepComplete(stepId);
      const isCurrentlyCompleted = formData.completedSteps.includes(stepId);

      if (shouldBeCompleted && !isCurrentlyCompleted) {
        markStepCompleted(stepId);
      } else if (!shouldBeCompleted && isCurrentlyCompleted) {
        markStepUncompleted(stepId);
      }
    });
  }, [formData, isStepComplete, markStepCompleted, markStepUncompleted, stepsConfig]);

  // Update steps based on form state and validation
  React.useEffect(() => {
    setSteps((prevSteps) => updateStepsArray(prevSteps, formData, currentStep, formData.completedSteps, getStepValidation));
  }, [currentStep, formData.completedSteps, formData, getStepValidation, setSteps]);

  // Navigate to review step in edit mode after lecture is loaded and steps are validated
  React.useEffect(() => {
    const shouldNavigate = isEditMode && 
                          isLectureLoaded && 
                          !isLoading && 
                          lectureId !== navigatedForLectureId &&
                          formData.currentStep !== 'review';
    
    // console.log("Navigation attempt:", { 
    //   isEditMode, 
    //   isLectureLoaded, 
    //   isLoading, 
    //   lectureId,
    //   navigatedForLectureId,
    //   shouldNavigate,
    //   canNavigate: canNavigateToStep('review'),
    //   completedSteps: formData.completedSteps,
    //   currentStep: formData.currentStep
    // });
    
    if (shouldNavigate) {
      // Small delay to ensure all step completion states are updated
      const timer = setTimeout(() => {
        // console.log("Timer executing - checking navigation conditions:", {
        //   canNavigate: canNavigateToStep('review'),
        //   completedSteps: formData.completedSteps,
        //   validationSummary: getValidationSummary()
        // });
        
        if (canNavigateToStep('review')) {
          // console.log("Navigating to review step for editing");
          navigateToStep('review');
          setNavigatedForLectureId(lectureId);
        } else {
          // console.log("Cannot navigate to review step - some required steps are not completed");
        }
      }, 100);
      
      return () => clearTimeout(timer);
    }
  }, [isEditMode, isLectureLoaded, isLoading, lectureId, navigatedForLectureId, canNavigateToStep, navigateToStep, formData.completedSteps, formData.currentStep, getValidationSummary]);

  // Activate highlight mode when on review step or in edit mode
  React.useEffect(() => {
    if (currentStep === 'review') {
      setHighlightIncompleteSteps(true);
    }
    // Also activate in edit mode
    if (isEditMode && isLectureLoaded) {
      setHighlightIncompleteSteps(true);
    }
  }, [currentStep, isEditMode, isLectureLoaded]);

  const handleStepChange = (selectedStep) => {
    console.log("Step/Substep selected:", selectedStep);

    let targetStepId;

    if (selectedStep.parentStepId) {
      // It's a substep - use the substep ID
      targetStepId = selectedStep.id;
    } else if (selectedStep.substeps && selectedStep.substeps.length > 0) {
      // It's a main step with substeps - navigate to first substep
      targetStepId = selectedStep.substeps[0].id;
    } else {
      // It's a main step without substeps
      targetStepId = selectedStep.id;
    }

    // console.log("Navigating to:", targetStepId);
    
    // Check if navigation is allowed before attempting
    if (!canNavigateToStep(targetStepId)) {
      console.log("Navigation blocked - step requires completion of prerequisites");
      return;
    }
    
    const success = navigateToStep(targetStepId);
    console.log("Navigation success:", success);
  };

  const handleStepComplete = (stepId) => {
    console.log("Step completed:", stepId);

    // Log validation state when step is completed
    const validationSummary = getValidationSummary();
    console.log("Validation summary on step completion:", validationSummary);
    console.log("Form ready to save:", isFormReadyToSave());
  };

  /**
   * Handle saving the lecture - this is passed to ReviewStep
   * The actual save operation happens here in the parent component
   */
  const handleSaveLecture = useCallback(async () => {
    try {
      const isUpdate = lectureId && lectureId !== 'new';

      // Transform form data to database format
      // Pass originalLectureData for updates to handle field deletions correctly
      const transformedData = transformFormToDBFormat(
        formData,
        companyId,
        eventId,
        currentIndustry?.id,
        isUpdate ? originalLectureData : null
      );

      console.log('Saving lecture with data:', transformedData);

      let savedLecture;
      if (isUpdate) {
        // Update existing lecture
        savedLecture = await updateLecture(lectureId, transformedData);
      } else {
        // Create new lecture
        savedLecture = await createLecture(transformedData);
      }

      console.log('Lecture saved successfully:', savedLecture);

      // Call the parent callback with the saved lecture data
      if (onSaveLecture) {
        onSaveLecture(savedLecture);
      }

      return savedLecture;
    } catch (error) {
      captureError(error, {
        feature: 'lecture-management',
        operation: 'save-lecture',
        consoleMessage: 'Error saving lecture:',
        context: {
          lectureId,
          eventId,
          companyId,
          isEdit: lectureId && lectureId !== 'new'
        }
      });
      throw error;
    }
  }, [formData, companyId, eventId, lectureId, currentIndustry?.id, originalLectureData, createLecture, updateLecture, onSaveLecture]);


  /**
   * Handle save as draft and close
   */
  const handleSaveAsDraft = useCallback(async () => {
    try {
      const isUpdate = lectureId && lectureId !== 'new';

      // Transform form data to database format
      // Pass originalLectureData for updates to handle field deletions correctly
      const transformedData = transformFormToDBFormat(
        formData,
        companyId,
        eventId,
        currentIndustry?.id,
        isUpdate ? originalLectureData : null
      );
      // Add draft flag
      transformedData.isDraft = true;

      console.log('Saving lecture as draft:', transformedData);

      let savedLecture;
      if (isUpdate) {
        // Update existing lecture
        savedLecture = await updateLecture(lectureId, transformedData);
      } else {
        // Create new lecture
        savedLecture = await createLecture(transformedData);
      }

      console.log('Draft saved successfully:', savedLecture);

      // Close the modal
      if (onCloseRequest) {
        onCloseRequest();
      }

      // Call the parent callback with the saved lecture data
      if (onSaveLecture) {
        onSaveLecture({ ...savedLecture, isDraft: true });
      }
    } catch (error) {
      captureError(error, {
        feature: 'lecture-management',
        operation: 'save-draft',
        consoleMessage: 'Error saving draft:',
        context: {
          lectureId,
          eventId,
          companyId
        }
      });
      // You might want to show an error message here
    }
  }, [formData, companyId, eventId, lectureId, currentIndustry?.id, originalLectureData, createLecture, updateLecture, onCloseRequest, onSaveLecture]);

  /**
   * Handle discard changes and close
   */
  const handleDiscardChanges = useCallback(() => {
    if (onCloseRequest) {
      onCloseRequest();
    }
  }, [onCloseRequest]);

  // Expose save function to parent component via ref
  useImperativeHandle(ref, () => ({
    saveLecture: handleSaveLecture
  }), [handleSaveLecture]);

  // Show loading spinner if loading lecture data
  if (isLoading) {
    return (
      <div className="d-flex flex-column justify-content-center align-items-center" style={{ height: "100vh" }}>
        <MDBSpinner style={{ width: '3rem', height: '3rem' }} className="mb-3">
          <span className="visually-hidden">{t("Loading...")}</span>
        </MDBSpinner>
        <p className="text-muted">{t('Loading lecture data')}</p>
      </div>
    );
  }

  // Show error if loading failed
  if (loadError) {
    return (
      <div className="d-flex flex-column justify-content-center align-items-center" style={{ height: "100vh" }}>
        <MDBAlert color="danger" className="mb-3">
          <h4 className="alert-heading">{t('Error')}</h4>
          <p>{loadError}</p>
          <hr />
          <p className="mb-0">{t('Please try again or contact support if the problem persists.')}</p>
        </MDBAlert>
      </div>
    );
  }

  return (
    <div className="d-flex flex-column" style={{ height: "100vh" }}>
      {/* Add header with title and close buttons if in modal mode */}
      {isModal && onCloseRequest && (
        <div className="d-flex justify-content-between align-items-center px-4 py-3 border-bottom bg-white">
          <div>
            <h5 className="mb-0">
              {isEditMode
                ? `${t('Edit ' + (getEntityLabel('singular') || 'lecture'))}: ${getLocalizedText(formData.title) || t('Untitled')}`
                : t('Create new ' + (getEntityLabel('singular') || 'lecture'))
              }
            </h5>
          </div>
          <div className="d-flex me-4">
          {!hasChanges ? (
            // Show only close button when no changes
            <MDBBtn color="danger" outline onClick={handleDiscardChanges}>
              <MDBIcon fas icon="times" className="me-2" />
              {t('Close')}
            </MDBBtn>
          ) : (
            // Show discard and save draft buttons when changes exist
            <>
              <MDBBtn color="link" className="text-danger" onClick={handleDiscardChanges}>
                <MDBIcon fas icon="times" className="me-2" />
                {t('Close & Discard')}
              </MDBBtn>
              <MDBBtn color="success" outline onClick={handleSaveAsDraft}>
                <MDBIcon fas icon="save" className="me-2" />
                {t('Save Draft')}
              </MDBBtn>
            </>
          )}
          </div>
        </div>
      )}
      
      {/* Main content area */}
      <div className="d-flex flex-grow-1" style={{ minHeight: 0 }}>
        {/* Navigation - fixed width, no scroll */}
        <div className="pe-5 border-end d-flex justify-content-center align-items-center" style={{ flexShrink: 0 }}>
          <StepperNavigation 
            steps={steps} 
            setSteps={setSteps} 
            onStepChange={handleStepChange} 
            activeStep={currentStep} 
            multiExpand={false} 
            size="xl" 
            canNavigateToStep={canNavigateToStep} 
            highlightIncomplete={highlightIncompleteSteps} 
          />
        </div>
        
        {/* Content area - scrollable */}
        <div className="flex-grow-1 px-4 py-4" style={{ overflow: "hidden" }}>
          <div className="h-100 overflow-auto">
            <StepRenderer 
              activeStep={currentStep} 
              onStepComplete={handleStepComplete} 
              onStepChange={handleStepChange} 
              companyId={companyId} 
              eventId={eventId} 
              lectureId={lectureId}
              onSaveLecture={handleSaveLecture}
              isSavingLecture={isSavingAPI}
            />
          </div>
        </div>
      </div>
      
      {isDevMode && (
        <div className="border-top bg-light p-3">
          <h5 className="text-danger mb-3">Debug Information (Dev Mode)</h5>
          <div className="row">
            <div className="col-md-6">
              <h6>Current Lecture Object:</h6>
              <pre className="bg-white p-3 rounded border" style={{ fontSize: "12px", maxHeight: "300px", overflow: "auto" }}>
                {JSON.stringify(formData, null, 2)}
              </pre>
            </div>
            <div className="col-md-6">
              <h6>Validation Results:</h6>
              <pre className="bg-white p-3 rounded border" style={{ fontSize: "12px", maxHeight: "300px", overflow: "auto" }}>
                {JSON.stringify(getValidationSummary(), null, 2)}
              </pre>
              <div className="mt-2">
                <strong>Form Ready to Save:</strong> <span className={isFormReadyToSave() ? "text-success" : "text-danger"}>{isFormReadyToSave() ? "Yes" : "No"}</span>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
});

// Component that provides steps config context
const LectureRegistrationWithStepsConfig = forwardRef(({ companyId, eventId, lectureId, onSaveLecture, onCloseRequest, isModal }, ref) => {
  const { t } = useTranslation();
  const { stepsConfig, validationSchema, isLoading } = useConfig();
  const [steps, setSteps] = React.useState(stepsConfig || []);

  // Store ref to content component to access its save function
  const contentRef = React.useRef();

  // Expose save function to parent component
  useImperativeHandle(ref, () => ({
    saveLecture: () => {
      if (contentRef.current?.saveLecture) {
        return contentRef.current.saveLecture();
      }
      return Promise.reject(new Error('Save function not available'));
    }
  }), []);

  // Update steps when configuration changes
  React.useEffect(() => {
    if (stepsConfig) {
      setSteps(stepsConfig);
    }
  }, [stepsConfig]);

  // Show loading spinner while configuration is loading
  if (isLoading || !validationSchema || !stepsConfig) {
    return (
      <MDBContainer className="d-flex justify-content-center align-items-center" style={{ minHeight: "400px" }}>
        <MDBSpinner role="status">
          <span className="visually-hidden">{t('Loading')}</span>
        </MDBSpinner>
      </MDBContainer>
    );
  }

  return (
    <StepsConfigProvider stepsConfig={steps}>
      <LectureRegistrationContent
        ref={contentRef}
        steps={steps}
        setSteps={setSteps}
        companyId={companyId}
        eventId={eventId}
        lectureId={lectureId}
        onSaveLecture={onSaveLecture}
        onCloseRequest={onCloseRequest}
        isModal={isModal}
      />
    </StepsConfigProvider>
  );
});

// Main export component with ConfigProvider and FormProvider wrapper
const Index = forwardRef(({ companyId, eventId, lectureId, eventConfig, onSaveLecture, onCloseRequest, isModal = false }, ref) => {
  // Use passed eventConfig if provided, otherwise fall back to eventId
  const actualEventId = eventId || eventConfig?.eventId || "cb7a916e-ed71-483a-8177-22532ab0b237";

  return (
    <LectureErrorBoundary onClose={onCloseRequest}>
      <ConfigProvider eventId={actualEventId} eventConfig={eventConfig} companyId={companyId}>
        <FormProvider>
          <ValidationProvider>
            <LectureRegistrationWithStepsConfig
              ref={ref}
              companyId={companyId}
              eventId={actualEventId}
              lectureId={lectureId}
              onSaveLecture={onSaveLecture}
              onCloseRequest={onCloseRequest}
              isModal={isModal}
            />
          </ValidationProvider>
        </FormProvider>
      </ConfigProvider>
    </LectureErrorBoundary>
  );
});

Index.displayName = 'LectureRegistration';

export default Index;